
import React, { useState, useEffect } from "react";
import DetailPageTemplate from "@/components/DetailPageTemplate";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fetchAllCampaigns } from "@/services/modalCampaigns";
import { ModalCampaign } from "@/types/modalCampaigns";
import CampaignList from "@/components/modal-campaigns/admin/CampaignList";
import CampaignForm from "@/components/modal-campaigns/admin/CampaignForm";
import CampaignStats from "@/components/modal-campaigns/admin/CampaignStats";

const ModalCampaignAdmin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("list");
  const [campaigns, setCampaigns] = useState<ModalCampaign[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedCampaign, setSelectedCampaign] = useState<ModalCampaign | null>(null);

  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        setIsLoading(true);
        const data = await fetchAllCampaigns();
        setCampaigns(data);
      } catch (error) {
        console.error('Error loading campaigns:', error);
        toast.error('Er is een fout opgetreden bij het laden van de campagnes');
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaigns();
  }, []);

  const handleEditCampaign = (campaign: ModalCampaign) => {
    setSelectedCampaign(campaign);
    setActiveTab("create");
  };

  const handleViewStats = (campaign: ModalCampaign) => {
    setSelectedCampaign(campaign);
    setActiveTab("stats");
  };

  const handleCreateSuccess = () => {
    toast.success('Campagne succesvol opgeslagen');
    setActiveTab("list");
    setSelectedCampaign(null);
    
    // Refresh campaigns list
    fetchAllCampaigns()
      .then(data => setCampaigns(data))
      .catch(error => {
        console.error('Error refreshing campaigns:', error);
        toast.error('Er is een fout opgetreden bij het vernieuwen van de lijst');
      });
  };

  const handleCancelCreate = () => {
    setActiveTab("list");
    setSelectedCampaign(null);
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case "stats":
        return selectedCampaign ? `Statistieken: ${selectedCampaign.title}` : 'Statistieken';
      case "create":
        return selectedCampaign ? 'Campagne bewerken' : 'Nieuwe campagne';
      default:
        return 'Alle campagnes';
    }
  };

  return (
    <DetailPageTemplate
      title="Nieuws+"
      description="Beheer nieuws en quizzen voor gebruikers"
      backButtonProps={{
        label: "Terug naar dashboard",
        onClick: () => window.history.back()
      }}
    >
      <Card className="mb-6">
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="list">Alle campagnes</TabsTrigger>
              <TabsTrigger value="create">{selectedCampaign ? 'Campagne bewerken' : 'Nieuwe campagne'}</TabsTrigger>
              {selectedCampaign && <TabsTrigger value="stats">Statistieken</TabsTrigger>}
            </TabsList>
            <TabsContent value="list">
              <CampaignList
                campaigns={campaigns}
                isLoading={isLoading}
                onEdit={handleEditCampaign}
                onViewStats={handleViewStats}
                onRefresh={() => {
                  setIsLoading(true);
                  fetchAllCampaigns()
                    .then(data => {
                      setCampaigns(data);
                      setIsLoading(false);
                    })
                    .catch(error => {
                      console.error('Error refreshing campaigns:', error);
                      toast.error('Er is een fout opgetreden bij het vernieuwen van de lijst');
                      setIsLoading(false);
                    });
                }}
                onCreateNew={() => {
                  setSelectedCampaign(null);
                  setActiveTab("create");
                }}
              />
            </TabsContent>
            <TabsContent value="create">
              <CampaignForm
                currentUser={user}
                existingCampaign={selectedCampaign}
                onSuccess={handleCreateSuccess}
                onCancel={handleCancelCreate}
              />
            </TabsContent>
            <TabsContent value="stats">
              {selectedCampaign && (
                <CampaignStats 
                  campaignId={selectedCampaign.id}
                  onBack={() => setActiveTab("list")}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </DetailPageTemplate>
  );
};

export default ModalCampaignAdmin;
